import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiFetch } from '../lib/api'
import type { EndUserPage, Product, UsageSummary } from '../lib/types'
import type { UsageFilters } from './useUsageFilters'
import { useProductStatus } from './useProductStatus'

/** `null` (no filter) stays out of the query string entirely; `[]` goes on it
 * as an empty value, which the API reads as "the admin deselected everything"
 * and answers with an empty page.
 *
 * Collapsing the two would silently turn a deselect-all into a select-all —
 * showing the whole account's usage to someone who asked to see none of it —
 * which is why this tests for `null` rather than for emptiness. */
function idsParam(name: string, value: string[] | null): string {
  return value === null ? '' : `&${name}=${value.join(',')}`
}

/** Everything the Usage page reads from the server, for the given filters.
 *
 * Takes the filters rather than owning them, so this hook has exactly one
 * job: turn "what is selected" into "what the server says". That also makes
 * it the only place that knows the query-string shape. */
export function useUsagePageData(
  product: Product,
  { tenantFilter, userFilter, month }: Pick<UsageFilters, 'tenantFilter' | 'userFilter' | 'month'>
) {
  const status = useProductStatus(product)
  // Usage is the full page for every status but Not Contracted — an Expired
  // product can still read what it consumed (MS-819; spec §4.1b). Nothing
  // below is fetched until the status says the page is full.
  const contracted = status.view('usage') === 'full'

  // Filter options list every tenant and every registered user, including
  // those with no usage this month — matching the prototype, whose dropdowns
  // are built from the full TENANTS/MCP_USERS lists rather than from whatever
  // the current chart happens to contain. They also combine independently:
  // neither narrows the other's options.
  const { data: endUserPage } = useQuery({
    queryKey: ['end-users', product, 'all'],
    queryFn: () =>
      apiFetch<EndUserPage>(`/api/v1/end-users?product=${encodeURIComponent(product)}&pageSize=200`),
    enabled: contracted,
  })
  const { data: tenants } = useQuery({
    queryKey: ['tenants'],
    queryFn: () => apiFetch<{ id: string; tenantName: string }[]>('/api/v1/tenants'),
    enabled: contracted,
  })

  const {
    data: summary,
    isPending,
    isError,
  } = useQuery({
    queryKey: ['usage-summary', product, month, tenantFilter, userFilter],
    queryFn: () =>
      apiFetch<UsageSummary>(
        `/api/v1/usage/summary?product=${encodeURIComponent(product)}` +
          (month ? `&month=${month}` : '') +
          idsParam('tenantIds', tenantFilter) +
          idsParam('endUserIds', userFilter)
      ),
    enabled: contracted,
    // The five KPIs and both breakdowns all come from this one response, so
    // holding the previous month's numbers on screen while the next loads is
    // better than blanking the whole page on every picker change.
    placeholderData: (previous) => previous,
  })

  const tenantOptions = useMemo(
    () => (tenants ?? []).map((tenant) => ({ value: tenant.id, label: tenant.tenantName })),
    [tenants]
  )
  const userOptions = useMemo(
    () =>
      (endUserPage?.items ?? []).map((u) => ({
        value: u.id,
        label: u.email ?? u.fullName ?? u.subjectId,
      })),
    [endUserPage]
  )

  return {
    status,
    contracted,
    summary,
    isPending,
    isError,
    tenantOptions,
    userOptions,
    // The month actually on screen: the admin's pick if they made one,
    // otherwise whatever the response says the account's own current month is.
    // Derived here rather than in the page because it needs both halves.
    selectedMonth: month ?? summary?.month ?? null,
  }
}
